import { useMemo } from 'react';
import { extractCategoryFromPathname, buildCorrectedPath } from '../utils/urlUtils';
import { deriveCategory } from '../utils/activityTransformUtils';

export interface WorkflowCategoryValidationResult {
  /** Whether the URL category matches the workflow's actual category */
  isValid: boolean;
  /** The corrected path if invalid, null otherwise */
  correctPath: string | null;
}

/**
 * Validates that the URL category matches the workflow's actual category.
 * Returns the correct path for redirection if there's a mismatch.
 * 
 * @param pathname - Current URL pathname
 * @param workflowTags - The workflow's tags array
 * @returns Validation result with isValid flag and correctPath if redirect needed
 * 
 * @example
 * // URL has /mfr/ but workflow is PPM
 * const { isValid, correctPath } = useWorkflowCategoryValidation(
 *   '/reports-and-analysis/space/123/mfr/456/report/789',
 *   ['PPM']
 * );
 * // isValid: false, correctPath: '/reports-and-analysis/space/123/ppm/456/report/789'
 */
export const useWorkflowCategoryValidation = (
  pathname: string,
  workflowTags: readonly string[] | undefined
): WorkflowCategoryValidationResult => {
  return useMemo(() => {
    const urlCategory = extractCategoryFromPathname(pathname);
    const workflowCategory = deriveCategory(workflowTags as string[] | undefined);

    // Fail open: if we can't determine either category, consider valid
    if (!urlCategory || !workflowCategory) {
      return { isValid: true, correctPath: null };
    }

    // Case-insensitive comparison
    if (urlCategory.toUpperCase() === workflowCategory.toUpperCase()) {
      return { isValid: true, correctPath: null };
    }

    // Mismatch: build corrected path using segment-based replacement
    const correctPath = buildCorrectedPath(pathname, workflowCategory);

    return { isValid: false, correctPath };
  }, [pathname, workflowTags]);
};
